import type { CompanyEntityData } from '@/components/ui/design-system/entities/company';
import type { JobEntityData } from '@/components/ui/design-system/entities/job';
import type { ContactEntityData } from '@/components/ui/design-system/entities/contact';

export type SearchIntent = 'jobs' | 'companies' | 'contacts';

export type ResultFilter = 'all' | 'companies' | 'jobs' | 'contacts';

export type FilterType = 'company' | 'location' | 'skill' | 'title';

export interface SearchFilterGroup {
  type: FilterType;
  /** Added explicitly by the user via the + menu. */
  values: string[];
  /** Detected automatically from the query text by the parse API. */
  autoValues: string[];
}

export type StageKey = 'understanding' | 'exactMatch' | 'similarExpansion' | 'companies' | 'jobs' | 'expandedJobs' | 'contacts' | 'answer';
export type StageStatus = 'pending' | 'running' | 'completed' | 'failed';
export type StageState = Record<StageKey, StageStatus>;

export interface ClassifiedQuery {
  raw: string;
  intent: SearchIntent;
  companies: string[];
  roles: string[];
  locations: string[];
  industries: string[];
  seniority: string[];
  similarCompanies?: string[];
  similarTitles?: string[];
  similarSkills?: string[];
}

/**
 * "Top match" = company anchor + the contacts and jobs we'd start with at
 * that company. Mirrors the report layout where the company is the
 * information unit and contacts/jobs are its supporting evidence.
 */
export interface TopMatch {
  company: CompanyEntityData;
  /** Initially-visible contacts at this company (the page-1 slice). */
  contacts: ContactEntityData[];
  /** Initially-visible jobs at this company (the page-1 slice). */
  jobs: JobEntityData[];
  /** Total counts so the "Show more" buttons know when to disappear. */
  totalContacts: number;
  totalJobs: number;
}

export interface JobWithContacts {
  job: JobEntityData;
  contacts: ContactEntityData[];
}

export type FeedItem =
  | { type: 'insight'; id: string; placement: 'top' | 'between' | 'bottom'; text: string }
  | { type: 'exactCompanySection'; id: string; companyName: string; commentary?: string }
  | { type: 'similarCompaniesSection'; id: string; companyNames: string[]; commentary?: string }
  | { type: 'topMatchesSection'; id: string; matches: TopMatch[]; commentary?: string }
  | { type: 'jobsSection'; id: string; jobs: JobWithContacts[]; total: number; commentary?: string }
  | { type: 'contactsSection'; id: string; contacts: ContactEntityData[]; total: number; commentary?: string }
  | { type: 'answerSection'; id: string; question: string; answer: string; isStreaming: boolean };

export interface SearchLogEntry {
  id: string;
  stageKey: StageKey;
  message: string;
  details?: string[];
}

export interface SearchRound {
  id: string;
  query: string;
  resumeTitle: string | null;
  classified: ClassifiedQuery | null;
  stages: StageState;
  feed: FeedItem[];
  logs: SearchLogEntry[];
  errored: boolean;
}

export interface SearchSnapshot {
  query: string;
  classified: ClassifiedQuery | null;
  stages: StageState;
  feed: FeedItem[];
  logs: SearchLogEntry[];
  errored: boolean;
}

export const INITIAL_STAGES: StageState = {
  understanding: 'pending',
  exactMatch: 'pending',
  similarExpansion: 'pending',
  companies: 'pending',
  jobs: 'pending',
  expandedJobs: 'pending',
  contacts: 'pending',
  answer: 'pending',
};

/**
 * Pagination contract for "Show more" buttons. Each call returns the next
 * page worth of items; callers append the result to their visible list.
 * In Phase 1 these are mocks with a simulated network delay; in Phase 2
 * they hit `/api/search/<entity>/page` re-running the same classifier.
 */
export interface SearchPagers {
  loadMoreCompanyContacts: (companyId: string, offset: number) => Promise<ContactEntityData[]>;
  loadMoreCompanyJobs: (companyId: string, offset: number) => Promise<JobEntityData[]>;
  loadMoreJobs: (offset: number) => Promise<JobWithContacts[]>;
  loadMoreContacts: (offset: number) => Promise<ContactEntityData[]>;
}
